import { type FormEvent, useEffect, useState } from "react";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import SchemaForm, {
  type EventSchema,
  equipmentLabel,
} from "@/components/SchemaForm";
import { api } from "@/lib/api";
import { formatDateTime, toLocalInputValue, useAsync } from "@/lib/hooks";
import type { Equipment, EventItem, EventList } from "@/types";

const EVENT_TYPES = [
  "rain",
  "soak",
  "drain_refill",
  "backwash",
  "season_open",
  "season_close",
  "solar_cover",
  "equipment_change",
  "liner_replacement",
  "other",
] as const;

type EventType = (typeof EVENT_TYPES)[number];

export default function Events() {
  const { data, error, loading, reload } = useAsync<EventList>(
    () => api.get<EventList>("/api/events"),
    [],
  );
  // Loaded here rather than per card: a page of events is a page of lookups
  // into the same short list, and the dialog wants it too.
  const equipment = useAsync<Equipment[]>(
    () => api.get<Equipment[]>("/api/equipment"),
    [],
  );
  const [adding, setAdding] = useState(false);

  const open = (data?.items ?? []).filter((e) => e.is_open);

  return (
    <AppShell
      title="Events"
      subtitle={data ? `${data.total} recorded` : undefined}
      action={
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="btn btn-primary px-3 py-2 text-sm"
        >
          Add
        </button>
      }
    >
      {error && <ErrorNote message={error} />}
      {loading && <p className="text-sm text-(--color-ink-muted)">Loading…</p>}

      {open.length > 0 && (
        <section className="mb-6">
          <h2 className="unit mb-2">Still open</h2>
          <ul className="space-y-2">
            {open.map((event) => (
              <li key={event.id}>
                <EventCard
                  event={event}
                  equipment={equipment.data}
                  onChanged={reload}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {data && data.items.length === 0 && (
        <EmptyState
          title="Nothing logged yet"
          body="Storms, drains, backwashes, opening and closing for the season — the things that explain a sudden change in the chart."
          action={
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="btn btn-primary"
            >
              Log an event
            </button>
          }
        />
      )}

      {data && data.items.length > 0 && (
        <ul className="space-y-2">
          {data.items
            .filter((e) => !e.is_open)
            .map((event) => (
              <li key={event.id}>
                <EventCard
                  event={event}
                  equipment={equipment.data}
                  onChanged={reload}
                />
              </li>
            ))}
        </ul>
      )}

      {adding && (
        <EventDialog
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      )}
    </AppShell>
  );
}

function EventCard({
  event,
  equipment,
  onChanged,
}: {
  event: EventItem;
  /** Null while still loading — see `PayloadRow`. */
  equipment: Equipment[] | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  // Null means the picker is closed. Opening it defaults to now, which is the
  // common case — but a solar cover comes off while you are carrying it, not
  // while you are holding a phone, so the time has to be editable. Stamping
  // `now` unconditionally recorded a cover as being on for however long it took
  // to get indoors and remember.
  const [endingAt, setEndingAt] = useState<string | null>(null);
  const payload = Object.entries(event.payload ?? {}).filter(
    ([key, value]) => key !== "event_type" && value !== null && value !== "",
  );

  async function close() {
    if (endingAt === null) {
      setEndingAt(toLocalInputValue(new Date()));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/events/${event.id}/close`, {
        ends_at: new Date(endingAt).toISOString(),
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium capitalize">
            {event.event_type.replace(/_/g, " ")}
          </p>
          <p className="unit mt-0.5">
            {formatDateTime(event.starts_at)}
            {event.ends_at && ` → ${formatDateTime(event.ends_at)}`}
            {event.is_open && " → ongoing"}
          </p>
        </div>
        {event.is_open && endingAt === null && (
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="btn btn-quiet shrink-0 px-3 py-2 text-sm"
          >
            End it
          </button>
        )}
      </div>

      {endingAt !== null && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label htmlFor={`ends-${event.id}`} className="text-sm">
            Ended at
          </label>
          <input
            id={`ends-${event.id}`}
            type="datetime-local"
            value={endingAt}
            onChange={(e) => setEndingAt(e.target.value)}
            className="field figure w-auto grow"
          />
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="btn btn-primary shrink-0 px-3 py-2 text-sm"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => setEndingAt(null)}
            className="btn btn-quiet shrink-0 px-3 py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      )}

      {payload.length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-(--color-ink-muted)">
          {payload.map(([key, value]) => (
            <PayloadRow
              key={key}
              name={key}
              value={value}
              equipment={equipment}
            />
          ))}
        </dl>
      )}

      {event.notes && <p className="mt-2 text-sm">{event.notes}</p>}
    </div>
  );
}

/**
 * One `key: value` from an event's payload.
 *
 * Most are plain. The exception is a reference into the equipment log, which
 * was being printed exactly as stored — "equipment id:
 * c8e4ebd6-d879-4cb4-a948-73ebb94f61b5" under a solar cover event. That is a
 * primary key on a screen, and it tells the owner nothing about which cover was
 * on. The dialog that wrote it already knew the answer was "Vevor UIRWAY
 * 16x32"; the card just never asked.
 *
 * Named by the equipment's own category rather than by the column, matching
 * what `specs._equipment_label` does server-side for the form: "cover", "old
 * liner". The category comes from the resolved row, so an equipment change —
 * whose two references are deliberately unfiltered — still names whatever was
 * actually linked.
 */
function PayloadRow({
  name,
  value,
  equipment,
}: {
  name: string;
  value: unknown;
  equipment: Equipment[] | null;
}) {
  const isReference = name.endsWith("_equipment_id") || name === "equipment_id";

  if (!isReference) {
    return (
      <div>
        <dt className="inline">{name.replace(/_/g, " ")}: </dt>
        <dd className="inline">{String(value)}</dd>
      </div>
    );
  }

  // Nothing at all until the list arrives, rather than a UUID that flashes and
  // is replaced. A raw id is never the answer to anything on this screen.
  if (equipment === null) return null;

  const item = equipment.find((row) => row.id === value);
  const base = name.replace(/_id$/, "");
  const label = (
    item ? base.replace("equipment", item.category) : base
  ).replace(/_/g, " ");

  return (
    <div>
      <dt className="inline">{label}: </dt>
      <dd className="inline">
        {/* Equipment is never deleted (non-negotiable #6), so a miss means the
            list failed to load or the link points at another vessel's row —
            both worth seeing, and neither worth a UUID. */}
        {item ? equipmentLabel(item) : "not in the equipment log"}
      </dd>
    </div>
  );
}

function EventDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [eventType, setEventType] = useState<EventType>("rain");
  const [schema, setSchema] = useState<EventSchema | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [startsAt, setStartsAt] = useState(() => toLocalInputValue(new Date()));
  // Blank means "still going". A span you are logging after the fact — the
  // cover that went on last night and came off at seven — should be recordable
  // in one pass, which previously it was not: the only way to end one was to be
  // standing there at the moment it ended and press a button that stamped now.
  const [endsAt, setEndsAt] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPayload({});
    setEndsAt("");
    void api
      .get<EventSchema>(`/api/events/schema/${eventType}`)
      .then(setSchema)
      .catch(() => setSchema(null));
  }, [eventType]);

  // Loaded once for the whole dialog rather than per field: an equipment change
  // has two pickers, and they are choosing from the same list.
  const equipment = useAsync<Equipment[]>(
    () => api.get<Equipment[]>("/api/equipment"),
    [],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const cleaned: Record<string, unknown> = { event_type: eventType };
    for (const [key, value] of Object.entries(payload)) {
      if (value !== null && value !== "") cleaned[key] = value;
    }

    try {
      await api.post("/api/events", {
        event_type: eventType,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        payload: cleaned,
        notes: notes || null,
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not log that event.",
      );
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Log an event"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-(--color-surface-raised) p-5 sm:rounded-2xl"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Log an event</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-(--color-ink-muted)"
          >
            Close
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="event-type" className="text-sm font-medium">
              What happened
            </label>
            <select
              id="event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventType)}
              className="field mt-1.5 capitalize"
            >
              {EVENT_TYPES.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="starts" className="text-sm font-medium">
              {schema?.is_span ? "Started" : "When"}
            </label>
            <input
              id="starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="field figure mt-1.5"
            />
          </div>

          {schema?.is_span && (
            <div>
              <label htmlFor="ends" className="text-sm font-medium">
                Ended
              </label>
              <input
                id="ends"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="field figure mt-1.5"
              />
              <p className="mt-1 text-xs text-(--color-ink-faint)">
                Leave blank if it is still going — you can end it from the list
                later, at whatever time it actually ended.
              </p>
            </div>
          )}
        </div>

        {equipment.error && (
          <p className="mt-3 text-xs text-(--color-ink-faint)">
            Could not load the equipment list, so any equipment picker below
            will be empty. The event still logs without it.
          </p>
        )}

        {schema && schema.fields.length > 0 && (
          <div className="mt-5">
            <h3 className="unit mb-2">Details</h3>
            <SchemaForm
              fields={schema.fields}
              values={payload}
              equipment={equipment.data ?? []}
              onChange={(name, value) =>
                setPayload((prev) => ({ ...prev, [name]: value }))
              }
            />
          </div>
        )}

        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="field mt-4 resize-y"
        />

        {error && (
          <div className="mt-4">
            <ErrorNote message={error} />
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="btn btn-primary mt-5 w-full"
        >
          {busy ? "Saving…" : "Log it"}
        </button>
      </form>
    </div>
  );
}
